import { useState, useCallback } from 'react';
import { ResolvedMatch, MeetingFormData, Employee } from '../interfaces/interface';
import { DocumentService } from '../services/DocumentService';
import { nameResolver } from '../utils/NameResolver';
import { EmployeeService } from '../services/EmployeeService';
import { logger, handleError } from '../../../../logger';
import { searchEmployeesFromFintechPapi, getEmployeeByLogin } from 'src/api/papi-service';
import { PapiEmployeeMapper } from '../utils/papiEmployeeMapper';

/** Default user-friendly error message for non-backend runtime errors */
const DEFAULT_DOCUMENT_ERROR =
  'Something went wrong while processing your document. Please ensure your file follows the expected ' +
  'meeting request template format and try again. If the issue persists, please create a support ticket.';

/**
 * Extracts the error message — shows backend error directly to the user.
 * Falls back to a generic message only if no message is available.
 */
function getUserFriendlyError(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return DEFAULT_DOCUMENT_ERROR;
}

/**
 * Batching mechanism to limit concurrent API calls
 * @param calls Array of promise-returning functions
 * @param batchSize Number of concurrent calls per batch (default: 5)
 * @returns Promise that resolves to array of all results
 */
async function batchApiCalls<T>(calls: (() => Promise<T>)[], batchSize = 5): Promise<T[]> {
  const results: T[] = [];
  for (let i = 0; i < calls.length; i += batchSize) {
    const batch = calls.slice(i, i + batchSize);
    const batchResults = await Promise.all(batch.map((call) => call()));
    results.push(...batchResults);
  }
  return results;
}

const lookupExecutiveAssistant = async (selectedEmployee: any, attendeeType: string): Promise<any | null> => {
  let selectedExecutiveAssistant = null;
  if (selectedEmployee) {
    try {
      selectedExecutiveAssistant = await EmployeeService.findExecutiveAssistant(selectedEmployee);

      if (selectedExecutiveAssistant) {
        logger.info(`Executive assistant found for ${attendeeType} attendee`, {
          hasEmployee: true,
          hasEA: true
        });
      }
    } catch (error) {
      logger.warn(`Failed to lookup executive assistant for ${attendeeType} attendee`, {
        hasEmployee: true,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    }
  }
  return selectedExecutiveAssistant;
};

export const useDocumentProcessing = () => {
  const [isProcessingDocument, setIsProcessingDocument] = useState(false);
  const [documentError, setDocumentError] = useState<string>('');
  const [documentDescription, setDocumentDescription] = useState<string>('');
  const [docxPreviewHTML, setDocxPreviewHTML] = useState<string>('');
  const [htmlTemplate, setHtmlTemplate] = useState<string>('');
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);

  const handleFormattedContentExtracted = useCallback((htmlContent: string) => {
    setDocxPreviewHTML(htmlContent);
    if (htmlContent && htmlContent.trim().length > 0) {
      setIsPreviewVisible(true);
    }
  }, []);

  const processDocument = useCallback(
    async (
      file: File,
      eventsApiContext: any,
      userAlias: string,
      manuallyEditedFields: Set<keyof MeetingFormData>,
      executiveAssistants: Employee[] = []
    ): Promise<{
      success: boolean;
      parsedAttendees?: ResolvedMatch[];
      formUpdates?: Partial<MeetingFormData>;
      htmlContent?: string;
    }> => {
      setIsPreviewVisible(false);
      setDocxPreviewHTML('');

      setIsProcessingDocument(true);
      setDocumentError('');

      try {
        const parseResult = await DocumentService.parseDocument(
          file,
          eventsApiContext,
          userAlias,
          'Parse this EA Meeting Request Template and extract the meeting details.'
        );

        if (!parseResult.success) {
          setDocumentError(getUserFriendlyError(new Error(parseResult.error || 'Failed to parse document')));
          return { success: false };
        }

        if (!parseResult.data) {
          setDocumentError('No meeting data found in document');
          return { success: false };
        }

        const parsedMeeting = parseResult.data;

        if (parseResult.htmlContent) {
          setHtmlTemplate(parseResult.htmlContent);
        }

        const formattedHtmlForDescription = parseResult.htmlContent || '';

        // Combine all attendee names for batch processing
        const allAttendeeNames = [...parsedMeeting.RequiredAttendees, ...parsedMeeting.OptionalAttendees].filter(
          (name) => name && name.trim().length > 0
        );

        logger.info('Starting batch employee data fetch', {
          totalNames: allAttendeeNames.length
        });

        // Batch fetch employee data for all names in groups of 5
        // Use Map to track which employees belong to which name
        const employeeDataByName = new Map<string, Employee[]>();
        if (allAttendeeNames.length > 0) {
          try {
            // Create batched API calls with smart lookup: try alias first, fall back to name search
            const searchCalls = allAttendeeNames.map((name) => async () => {
              // Try alias lookup first
              const aliasResponse = await getEmployeeByLogin(name);

              if (aliasResponse.employee) {
                // Found via alias - return single employee as array
                logger.info('Employee found via alias lookup', { name });
                return { name, employees: [PapiEmployeeMapper.mapToEmployee(aliasResponse.employee)] };
              }

              // No alias match - fall back to name search
              logger.info('Alias lookup failed, falling back to name search', { name });
              const searchResponse = await searchEmployeesFromFintechPapi(name, 15);
              return { name, employees: PapiEmployeeMapper.mapToEmployees(searchResponse.employees || []) };
            });

            const batchResults = await batchApiCalls(searchCalls, 5);

            // Store results in Map keyed by name
            batchResults.forEach((result) => {
              employeeDataByName.set(result.name, result.employees);
            });

            logger.info('Employee data fetched successfully', {
              totalNames: employeeDataByName.size
            });
          } catch (error) {
            handleError('Failed to fetch employee data', error);
            setDocumentError('We had trouble looking up some attendee names from the document. Please try again or add the attendees manually.');
            return { success: false };
          }
        }

        // Process attendees using fetched employee data
        const parsedAttendees: ResolvedMatch[] = [];
        let nextId = 1;

        // Process required attendees
        for (const name of parsedMeeting.RequiredAttendees) {
          // Get employees specific to this name only
          const nameSpecificEmployees = employeeDataByName.get(name) || [];
          const matches = nameResolver.findMatchesWithEmployeeData(name, nameSpecificEmployees);
          const selectedEmployee = matches.selectedEmployee;

          // Lookup executive assistant using helper function
          const selectedExecutiveAssistant = await lookupExecutiveAssistant(selectedEmployee, 'required');

          parsedAttendees.push({
            id: `doc-${nextId++}`,
            inputName: name,
            matches: matches.matches,
            selectedEmployee,
            selectedExecutiveAssistant,
            status: matches.status,
            priority: 'required',
            resolvedEmail: selectedEmployee?.employee_internal_email_address || ''
          });
        }

        // Process optional attendees
        for (const name of parsedMeeting.OptionalAttendees) {
          // Get employees specific to this name only
          const nameSpecificEmployees = employeeDataByName.get(name) || [];
          const matches = nameResolver.findMatchesWithEmployeeData(name, nameSpecificEmployees);
          const selectedEmployee = matches.selectedEmployee;

          // Lookup executive assistant using helper function
          const selectedExecutiveAssistant = await lookupExecutiveAssistant(selectedEmployee, 'optional');

          parsedAttendees.push({
            id: `doc-${nextId++}`,
            inputName: name,
            matches: matches.matches,
            selectedEmployee,
            selectedExecutiveAssistant,
            status: matches.status,
            priority: 'optional',
            resolvedEmail: selectedEmployee?.employee_internal_email_address || ''
          });
        }

        const formUpdates: Partial<MeetingFormData> = {
          subject: !manuallyEditedFields.has('subject') ? parsedMeeting.Subject : undefined,
          description: !manuallyEditedFields.has('description') ? formattedHtmlForDescription : undefined
        };

        logger.info('Document processed successfully', {
          requiredAttendeesCount: parsedMeeting.RequiredAttendees.length,
          optionalAttendeesCount: parsedMeeting.OptionalAttendees.length,
          hasSubject: !!parsedMeeting.Subject,
          hasDescription: !!formattedHtmlForDescription
        });

        return {
          success: true,
          parsedAttendees,
          formUpdates,
          htmlContent: parseResult.htmlContent
        };
      } catch (error) {
        handleError('Document processing failed', error);
        setDocumentError(getUserFriendlyError(error));
        return { success: false };
      } finally {
        setIsProcessingDocument(false);
      }
    },
    []
  );

  const resetDocumentState = useCallback(() => {
    setIsProcessingDocument(false);
    setDocumentError('');
    setDocumentDescription('');
    setDocxPreviewHTML('');
    setHtmlTemplate('');
    setIsPreviewVisible(false);
  }, []);

  return {
    isProcessingDocument,
    documentError,
    documentDescription,
    docxPreviewHTML,
    htmlTemplate,
    isPreviewVisible,
    handleFormattedContentExtracted,
    processDocument,
    resetDocumentState,
    setDocumentDescription,
    setIsPreviewVisible,
    setDocumentError
  };
};
